/**
 * IRI namespaces. `dpem` is the ontology namespace; `schema` and `rdfs` are
 * borrowed for the two terms that already have a well-known meaning (label,
 * description) so exported graphs are not gratuitously private.
 */
export const NAMESPACES = {
  dpem: 'https://dpem.example.org/ontology#',
  schema: 'http://schema.org/',
  rdfs: 'http://www.w3.org/2000/01/rdf-schema#',
} as const;

export type NamespacePrefix = keyof typeof NAMESPACES;

export const RDF_TYPE_IRI = 'http://www.w3.org/1999/02/22-rdf-syntax-ns#type';

const NAMESPACE_ENTRIES = Object.entries(NAMESPACES) as ReadonlyArray<[NamespacePrefix, string]>;

/**
 * Expands a compact IRI against the ontology namespaces. Values whose prefix is
 * not one of ours (`stage:S0`, `act:S2.1`, …) are absolute IRIs in their own
 * right as far as JSON-LD is concerned, so they are returned untouched — that is
 * exactly what keeps the legacy `@id` values byte-stable.
 */
export function expandIri(value: string): string {
  const separator = value.indexOf(':');
  if (separator <= 0) return value;
  const prefix = value.slice(0, separator);
  for (const [candidate, iri] of NAMESPACE_ENTRIES) {
    if (candidate === prefix) return iri + value.slice(separator + 1);
  }
  return value;
}

/** Compacts a full IRI back to `prefix:local` when it sits in a known namespace. */
export function compactIri(iri: string): string {
  for (const [prefix, base] of NAMESPACE_ENTRIES) {
    if (iri.startsWith(base)) return `${prefix}:${iri.slice(base.length)}`;
  }
  return iri;
}
